import React from "react";
import { arrayOf, object, shape, string, any, bool } from "prop-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroLead from "@insightfulscience/atomic-react/Hero/Lead";
import Breadcrumbs from "@insightfulscience/atomic-react/Breadcrumbs";
import BreadcrumbItem, {
	BreadcrumbItemLink,
} from "@insightfulscience/atomic-react/Breadcrumbs/Item";
import GuidePageTemplate from "@insightfulscience/atomic-react/GuidePageTemplate";
import GuidePageTemplateSidebar from "@insightfulscience/atomic-react/GuidePageTemplate/Sidebar";
import GuidePageTemplateMain from "@insightfulscience/atomic-react/GuidePageTemplate/Main";
import GuidePageTemplateNav, {
	GuidePageTemplateNavItem,
	GuidePageTemplateNavItemLink,
} from "@insightfulscience/atomic-react/GuidePageTemplate/Nav";
import { scrollToHeading } from "@insightfulscience/atomic-react/utils/Guides";
import { BackToTopButton } from "@insightfulscience/atomic-react/r2/BackToTopButton";
import Button from "@insightfulscience/atomic-react/Button";
import Icon from "@insightfulscience/atomic-react/Icon";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import NextLink from "@insightfulscience/smart-react/NextLink";
import { useLocale } from "@insightfulscience/smart-react/i18";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import Link from "@insightfulscience/atomic-react/Link";
import Image from "@insightfulscience/atomic-react/Image";
import richTextTypes from "../../richTextTypes/default";
import { generateHeadingId, sidebarNavigation } from "../../richTextTypes/default/utils";
import { BRAND_CODE_LA, BRAND_NAMES, GUIDE_TYPES } from "../../constants";
import SectionHero from "./components/SectionHero";
import renderBlockByType from "../../utils/renderBlockByType";
import AlertPreviewMode from "../../ui/AlertPreviewMode";
import RecommendedVideos from "../components/RecommendedVideos";
import BackLink from "../components/BackLink";

const getLeadContent = (brandCode, post) =>
	brandCode === BRAND_CODE_LA ? post.subtitle : post.lead;

const GuidePage = ({ isPreview, post, pageProps }) => {
	const [t] = useLocale("guides");
	const {
		siteDomain,
		breadcrumbs = [],
		brandCode,
		pageType = GUIDE_TYPES.Guide,
		backLink,
	} = pageProps;
	const {
		title,
		metaDescription = "",
		slug,
		body,
		toCLink = "",
		toCDownloadEbookLink = "",
		thumbnail,
		type = pageType,
		additionalResources = [],
		recommendedVideos = [],
		academy = false,
	} = post;

	const thumbnailUrl = thumbnail?.fields?.file?.url || "";
	const navigation = sidebarNavigation(body);
	const totalHeading2 = body.content.reduce((total, current) => {
		return total + (current.nodeType === "heading-2" ? 1 : 0);
	}, 0);
	const isSidebar = totalHeading2 > 0;
	const brandName = BRAND_NAMES[brandCode];
	const heroLead = getLeadContent(pageProps.brandCode, post);
	const ToC = toCLink || toCDownloadEbookLink;

	return (
		<>
			<PageMeta title={`${title} - ${brandName}`} description={metaDescription}>
				<PageMeta.OGP
					title={`${title} - ${brandName}`}
					description={metaDescription}
					url={`${siteDomain}/guides/${slug}`}
					image={thumbnailUrl || t("META_IMAGE")}
					imageSecureUrl={thumbnailUrl || t("META_IMAGE_SECURE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Twitter
					card={t("META_TWITTER_CARD")}
					site={t("META_TWITTER_SITE")}
					creator={t("META_TWITTER_CREATOR")}
					title={`${title} - ${brandName}`}
					description={metaDescription}
					image={thumbnailUrl || t("META_TWITTER_IMAGE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Canonical href={`${siteDomain}/guides/${slug}`} />
			</PageMeta>
			{isPreview ? <AlertPreviewMode /> : null}
			<SectionHero brandCode={brandCode}>
				{backLink && <BackLink backLink={backLink} />}
				{breadcrumbs && breadcrumbs.length && !academy ? (
					<Breadcrumbs>
						{breadcrumbs.map(breadcrumb => (
							<BreadcrumbItem key={breadcrumb.title}>
								<NextLink Component={BreadcrumbItemLink} href={breadcrumb.href}>
									{breadcrumb.title}
								</NextLink>
							</BreadcrumbItem>
						))}
						<BreadcrumbItem>{title}</BreadcrumbItem>
					</Breadcrumbs>
				) : null}
				{academy && (
					<HeroRow center>
						<HeroCol md={4} spacing={{ mb: 10 }}>
							<Link href={t("ACADEMY_LINK")}>
								<Image src={t("ACADEMY_LOGO")} isBaseUrl />
							</Link>
						</HeroCol>
					</HeroRow>
				)}

				<HeroTitle>{title}</HeroTitle>
				{heroLead && <HeroLead>{heroLead}</HeroLead>}
			</SectionHero>
			<GuidePageTemplate sidebar={isSidebar} type={type}>
				{isSidebar ? (
					<>
						<GuidePageTemplateSidebar>
							{ToC && (
								<GuidePageTemplateNavItem>
									<Button.Link size="s" href={ToC} target="_blank">
										<Icon name="arrow-to-bottom" />
										{t("DOWNLOAD_AS_EBOOK")}
									</Button.Link>
								</GuidePageTemplateNavItem>
							)}
							<GuidePageTemplateNav>
								{navigation.map(item => {
									return (
										<GuidePageTemplateNavItem key={item}>
											<GuidePageTemplateNavItemLink
												href={`#${generateHeadingId(item)}`}
												onClick={e => scrollToHeading(e)}
											>
												{item}
											</GuidePageTemplateNavItemLink>
										</GuidePageTemplateNavItem>
									);
								})}
							</GuidePageTemplateNav>
						</GuidePageTemplateSidebar>
						<GuidePageTemplateMain>
							{documentToReactComponents(body, richTextTypes)}
						</GuidePageTemplateMain>
					</>
				) : (
					<GuidePageTemplateMain sidebar={isSidebar}>
						{documentToReactComponents(body, richTextTypes)}
					</GuidePageTemplateMain>
				)}
			</GuidePageTemplate>
			{recommendedVideos.length > 0 && (
				<RecommendedVideos brandCode={brandCode} videos={recommendedVideos} />
			)}
			{additionalResources.map(renderBlockByType(slug))}
			<BackToTopButton />
		</>
	);
};

GuidePage.propTypes = {
	isPreview: bool,
	post: shape({
		title: string.isRequired,
		subtitle: string,
		metaDescription: string,
		slug: string.isRequired,
		lead: string,
		body: object, // eslint-disable-line react/forbid-prop-types,
		toCLink: string,
		toCDownloadEbookLink: string,
	}),
	pageProps: shape({
		resources: any, // eslint-disable-line react/forbid-prop-types,
		siteDomain: string.isRequired,
		brandCode: string,
		breadcrumbs: arrayOf(
			shape({
				title: string.isRequired,
				href: string.isRequired,
			}),
		),
	}),
};

export default GuidePage;
